import { DefaultMap } from "@glideapps/ts-necessities";

import { SerializedApp } from "common/components/SerializedApp";
import { MaterialGlideAppState } from "webapp/chrome/smart/material-glide-app/material-glide-app";

type MutateStateFunctions = {
    readonly undo: () => void;
    readonly redo: () => void;
};

interface RestorableAppState {
    readonly appID: string;
    readonly serializedApp: SerializedApp;
    readonly appState: MaterialGlideAppState;
}

export interface UndoAppModificationPayload extends RestorableAppState {
    readonly mutateStateFunctions: MutateStateFunctions[];
}

interface AppUndoState {
    past: UndoAppModificationPayload[];
    future: UndoAppModificationPayload[];
}

const undoStates = new DefaultMap<string, AppUndoState>(() => ({ past: [], future: [] }));

const maxHistoryLength = 50;

export function pushUndoState(restorePayload: RestorableAppState): void {
    const state = undoStates.get(restorePayload.appID);
    state.past.push({ ...restorePayload, mutateStateFunctions: [] });
    while (state.past.length > maxHistoryLength) {
        state.past.shift();
    }
    state.future = [];
}

export function pushRestoreStateFunction(appID: string, undo: () => void, redo: () => void): void {
    const { past } = undoStates.get(appID);
    if (past.length === 0) return;
    const lastAction = past[past.length - 1];
    lastAction.mutateStateFunctions.push({ undo, redo });
}

export function applyUndo(
    currentState: RestorableAppState,
    dispatchUndo: (payload: UndoAppModificationPayload) => void
): void {
    const { past, future } = undoStates.get(currentState.appID);
    const restoreAction = past.pop();
    if (restoreAction === undefined) return;

    future.push({ ...currentState, mutateStateFunctions: restoreAction.mutateStateFunctions });
    dispatchUndo(restoreAction);
}

export function applyRedo(
    currentState: RestorableAppState,
    dispatchRedo: (payload: UndoAppModificationPayload) => void
): void {
    const { past, future } = undoStates.get(currentState.appID);
    const restoreAction = future.pop();
    if (restoreAction === undefined) return;

    past.push({ ...currentState, mutateStateFunctions: restoreAction.mutateStateFunctions });
    dispatchRedo(restoreAction);
}

export function clearUndo(appID: string): void {
    undoStates.get(appID).past = [];
}

export function resetUndo(): void {
    undoStates.clear();
}

export function isPastEmpty(currentState: RestorableAppState): boolean {
    const { past } = undoStates.get(currentState.appID);

    return past.length === 0;
}

export function isFutureEmpty(currentState: RestorableAppState): boolean {
    const { future } = undoStates.get(currentState.appID);

    return future.length === 0;
}
